import React from "react";

import { ButtonControlSize, LinkControlType, LinkControlUnderline } from "../../Utilities/FormDesignConstants";

import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { FormControl } from "../FormControl";
import { StyledLink } from "../../../CommonUI/BuildlingBlocks/StyledLink";
import { PropertyEditorSelect } from "../../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { IFormActionComponent } from "../../Structure/IFormAction";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";

export class LinkFormControl extends FormControl implements IFormActionComponent {
    readonly __type: string = "Link";
    label: string = "Test Link";
    type: LinkControlType = LinkControlType.URL;
    linkUnderline: LinkControlUnderline = LinkControlUnderline.HOVER;
    size?: ButtonControlSize;
    valueName?: string;

    useNameAsOutcomeName: boolean = true;
    outcomeName: string = "";

    onClick = () => {
        this.getFormContext()?.outcomeTriggered(this.getOutcomeName());
    };

    getOutcomeName = () => {
        if (this.useNameAsOutcomeName) return this.label;
        return this.outcomeName;
    };

    render() {
        let size: "medium" | "small" | "large" = this.size
            ? this.size
            : this.getFormContext()?.form?.defaultButtonSize ?? "medium";

        let underline: "hover" | "always" | "none" = this.getFormContext()?.form?.defaultLinkUnderline ?? "hover";

        if (this.linkUnderline) underline = this.linkUnderline;

        return (
            <>
                {this.type === LinkControlType.ACTION && (
                    <StyledLink
                        size={size}
                        underline={underline}
                        data-testid="xlink-action"
                        onClick={this.onClick}
                        ref={this.ref}
                    >
                        {this.label}
                    </StyledLink>
                )}

                {this.type === LinkControlType.URL && (
                    <StyledLink
                        size={size}
                        underline={underline}
                        data-testid="xlink-url"
                        href={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                        ref={this.ref}
                    >
                        {this.label}
                    </StyledLink>
                )}
            </>
        );
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (!this.label) {
            issues.push(new ValidationIssue("Label is required", "label", this.id));
        }

        return issues;
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        return issues;
    }

    renderEditUI(): JSX.Element | null {
        return <XLinkEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "label",
            "type",
            "linkUnderline",
            "size",
            "valueName",
            "useNameAsOutcomeName",
            "outcomeName",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "label",
            "type",
            "linkUnderline",
            "size",
            "valueName",
            "useNameAsOutcomeName",
            "outcomeName",
        );
    }
}

function XLinkEditUI(props: { editMe: LinkFormControl }) {
    return (
        <UISpacedBoxContent>
            <PropertyEditorText
                editObject={props.editMe}
                propertyName="label"
                label="Label"
            />
            <PropertyEditorBoolean
                editObject={props.editMe}
                label="Use name as outcomeName"
                propertyName={"useNameAsOutcomeName"}
            />

            <PropertyEditorCollapse
                editObject={props.editMe}
                collapseLogicOrField={"useNameAsOutcomeName"}
                showIfFalse={true}
            >
                <PropertyEditorText
                    editObject={props.editMe}
                    propertyName={"outcomeName"}
                    label="Outcome Name"
                />
            </PropertyEditorCollapse>
            <PropertyEditorSelect
                editObject={props.editMe}
                propertyName="size"
                options={[ButtonControlSize.SMALL, ButtonControlSize.STANDARD, ButtonControlSize.LARGE]}
                label="Size"
            />
            <PropertyEditorSelect
                editObject={props.editMe}
                propertyName="type"
                options={[LinkControlType.URL, LinkControlType.ACTION]}
                label="Type"
            />
            <PropertyEditorSelect
                editObject={props.editMe}
                propertyName="linkUnderline"
                options={[LinkControlUnderline.ALWAYS, LinkControlUnderline.HOVER, LinkControlUnderline.NONE]}
                label="Underline"
            />
            <PropertyEditorCollapse
                editObject={props.editMe}
                collapseLogicOrField={() => props.editMe.type === LinkControlType.URL}
                observeProperties={["type"]}
            >
                <PropertyEditorText
                    editObject={props.editMe}
                    propertyName="valueName"
                    label="Value Name"
                />
            </PropertyEditorCollapse>
        </UISpacedBoxContent>
    );
}
